import { useMemo, useState } from 'react'
import { useAuth } from '../lib/auth'
import { useContacts, useSavedHotels, useVenues } from '../lib/database'
import type { VenueEntry } from '../lib/database'
import { useIsMobile } from '../lib/useMediaQuery'
import type { Contact, WithId } from '../types/models'
import Icon from '../components/Icon'
import MobileHeader, { MobileScreen } from '../components/mobile/MobileHeader'
import ContactDialog from '../dialogs/ContactDialog'
import VenueDialog from '../dialogs/VenueDialog'
import { ContactList, HotelList, SECTIONS, VenueList } from './database/lists'
import type { Section } from './database/lists'
import SearchField from '../components/SearchField'

/**
 * The address book.
 *
 * Everything you'd otherwise dig out of an old email: the people, the rooms,
 * and the hotels you've stayed in. One screen, three sections, one search — the
 * desktop and phone versions share the lists and differ only in chrome.
 */
export default function Database() {
  const { uid } = useAuth()
  const isMobile = useIsMobile()

  const [section, setSection] = useState<Section>('contacts')
  const [query, setQuery] = useState('')
  const [role, setRole] = useState<string | null>(null)
  const [city, setCity] = useState<string | null>(null)

  const [editingContact, setEditingContact] = useState<WithId<Contact> | null>(null)
  const [addingContact, setAddingContact] = useState(false)
  const [editingVenue, setEditingVenue] = useState<VenueEntry | null>(null)
  const [addingVenue, setAddingVenue] = useState(false)

  const { contacts, roles, loading: contactsLoading } = useContacts(uid)
  const { venues, loading: venuesLoading } = useVenues(uid)
  const { hotels, loading: hotelsLoading } = useSavedHotels(uid)

  const cities = useMemo(() => {
    const set = new Set(venues.map((venue) => venue.city).filter(Boolean))
    return Array.from(set).sort()
  }, [venues])

  const counts = {
    contacts: contacts.length,
    venues: venues.length,
    hotels: hotels.length,
  }

  function add() {
    if (section === 'contacts') setAddingContact(true)
    if (section === 'venues') setAddingVenue(true)
  }

  const lists = (
    <>
      {section === 'contacts' && (
        <ContactList
          contacts={contacts}
          query={query}
          role={role}
          loading={contactsLoading}
          canEdit
          onEdit={setEditingContact}
          onAdd={() => setAddingContact(true)}
        />
      )}
      {section === 'venues' && (
        <VenueList
          venues={venues}
          query={query}
          city={city}
          loading={venuesLoading}
          canEdit
          onEdit={setEditingVenue}
          onAdd={() => setAddingVenue(true)}
        />
      )}
      {section === 'hotels' && (
        <HotelList hotels={hotels} query={query} loading={hotelsLoading} />
      )}
    </>
  )

  const filters = (
    <>
      <SearchField
        label={`Search ${section}`}
        value={query}
        onChange={setQuery}
        width={isMobile ? '100%' : 320}
      />

      {section === 'contacts' && roles.length > 0 && (
        <FilterChips
          options={roles}
          selected={role}
          onSelect={setRole}
          allLabel="All roles"
        />
      )}

      {section === 'venues' && cities.length > 1 && (
        <FilterChips
          options={cities}
          selected={city}
          onSelect={setCity}
          allLabel="Everywhere"
        />
      )}
    </>
  )

  const dialogs = (
    <>
      {(addingContact || editingContact) && uid && (
        <ContactDialog
          open
          contact={editingContact}
          ownerId={uid}
          onClose={() => {
            setAddingContact(false)
            setEditingContact(null)
          }}
        />
      )}
      {(addingVenue || editingVenue) && uid && (
        <VenueDialog
          open
          venue={editingVenue}
          ownerId={uid}
          onClose={() => {
            setAddingVenue(false)
            setEditingVenue(null)
          }}
        />
      )}
    </>
  )

  /* ------------------------------------------------------------- mobile */

  if (isMobile) {
    return (
      <>
        <MobileHeader
          title="Database"
          back={false}
          action={
            section === 'hotels'
              ? undefined
              : { icon: 'plus', label: `Add ${section.slice(0, -1)}`, accent: true, onClick: add }
          }
        />
        <MobileScreen>
          <SegmentedSections section={section} counts={counts} onChange={setSection} />
          <div className="stack" style={{ gap: 10 }}>
            {filters}
          </div>
          {lists}
        </MobileScreen>
        {dialogs}
      </>
    )
  }

  /* ------------------------------------------------------------ desktop */

  return (
    <div style={{ padding: '30px var(--gutter) 60px' }}>
      <div className="sections" style={{ maxWidth: 1100, margin: '0 auto', gap: 22 }}>
        <div className="spread" style={{ alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <nav className="row scroll-x" style={{ gap: 22 }}>
            {SECTIONS.map((entry) => (
              <button
                key={entry.id}
                onClick={() => setSection(entry.id)}
                className="row"
                style={{
                  gap: 8,
                  alignItems: 'baseline',
                  color:
                    section === entry.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                <span
                  className="t-display-l"
                  style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2rem)' }}
                >
                  {entry.label}
                </span>
                <span className="t-spec-caption faint">{counts[entry.id]}</span>
              </button>
            ))}
          </nav>

          {section !== 'hotels' && (
            <button className="btn btn-primary" onClick={add}>
              <Icon name="plus" size={16} />
              {section === 'contacts' ? 'Add contact' : 'Add venue'}
            </button>
          )}
        </div>

        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {filters}
        </div>

        {lists}
      </div>

      {dialogs}
    </div>
  )
}

/* ----------------------------------------------------------------- pieces */

function SegmentedSections({
  section,
  counts,
  onChange,
}: {
  section: Section
  counts: Record<Section, number>
  onChange: (next: Section) => void
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${SECTIONS.length}, 1fr)`,
        gap: 3,
        padding: 3,
        background: 'var(--panel-raised)',
        borderRadius: 'var(--radius-control)',
      }}
    >
      {SECTIONS.map((entry) => {
        const selected = section === entry.id
        return (
          <button
            key={entry.id}
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(entry.id)}
            style={{
              padding: '9px 6px',
              borderRadius: 7,
              fontSize: '0.85rem',
              fontWeight: selected ? 600 : 500,
              background: selected ? 'var(--panel)' : 'transparent',
              color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: selected ? 'var(--shadow-card)' : 'none',
            }}
          >
            {entry.label}
            <span className="t-spec-caption faint" style={{ marginLeft: 6 }}>
              {counts[entry.id]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * A horizontal rail of filters.
 *
 * Outlined rather than filled when selected, matching the date pills — a solid
 * accent chip would be the loudest thing on a page whose job is to be scanned.
 */
function FilterChips({
  options,
  selected,
  onSelect,
  allLabel,
}: {
  options: string[]
  selected: string | null
  onSelect: (next: string | null) => void
  allLabel: string
}) {
  return (
    <div className="row scroll-x" style={{ gap: 7, minWidth: 0 }}>
      {[null, ...options].map((option) => {
        const isSelected = selected === option
        return (
          <button
            key={option ?? '__all'}
            onClick={() => onSelect(option)}
            aria-pressed={isSelected}
            style={{
              flex: 'none',
              padding: '6px 12px',
              borderRadius: 'var(--radius-chip)',
              fontSize: '0.8rem',
              fontWeight: isSelected ? 600 : 400,
              whiteSpace: 'nowrap',
              background: 'var(--panel)',
              border: `${isSelected ? '2px' : '1px'} solid ${
                isSelected ? 'var(--accent)' : 'var(--hairline)'
              }`,
              color: 'var(--text-primary)',
            }}
          >
            {option ?? allLabel}
          </button>
        )
      })}
    </div>
  )
}
